import { AuBankAccountElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Loading, TextInput, Typography } from "@/components/atoms";
import { getPhpHostUrl } from "@/helpers/apiHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useCountryConfig, useOperator, usePage } from "@/hooks";
import { useGetDebitsSecretMutation, useLazyGetDebitsDetailsQuery } from "@/redux/apis/payment/stripe/stripeApi";
import { useAppSelector } from "@/redux/hooks";
import { stripeSelector } from "@/redux/slices/payment/selectors";
import { addToast } from "@/utils";
import { auBankAccountOptions } from "../fixtures";
import { PlatformFeeCardEditor } from "./PlatformFeeCardEditor";

export const DebitsEditor = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { pathname } = usePage();
  const { country } = useOperator();
  const { config } = useCountryConfig(country);
  const url = `${getPhpHostUrl()}/dashboard${pathname}`;

  const [getDebitsDetails] = useLazyGetDebitsDetailsQuery();
  const [getDebitsSecret, { isLoading }] = useGetDebitsSecretMutation();
  const {
    debits: { clientSecret },
  } = useAppSelector(stripeSelector);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    getDebitsSecret({
      refresh_url: `${url}?refresh=1`,
      redirect_url: url,
    })
      .unwrap()
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A card-only country (CH) has no debit rail at all: platform fees come off a card on file.
  if (config.platformFeeCard) {
    return <PlatformFeeCardEditor />;
  }

  /**
   * Gated on whether the country has a Stripe direct-debit method wired up, rather than on a
   * country list. GB has none until Bacs is live, so it keeps landing here — the correct interim
   * state, and one that stops being reached the moment the method is configured.
   */
  if (!config.stripeDebitPaymentMethod) {
    return (
      <div className="flex flex-col gap-y-3 rounded-lg bg-neutral-surface-gray p-5 lg:w-[573px]">
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          Direct debit setup is not available for your region. Payment collection is managed through Stripe.
        </Typography>
        <Button variant="secondary" onClick={() => navigate("../")}>
          Back
        </Button>
      </div>
    );
  }

  const handleSubmitAu = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    const auBankAccount = elements.getElement(AuBankAccountElement);
    if (!auBankAccount || !fullName || !email) return;

    setIsSubmitting(true);
    const result = await stripe.confirmAuBecsDebitSetup(clientSecret, {
      payment_method: {
        au_becs_debit: auBankAccount,
        billing_details: {
          name: fullName,
          email,
        },
      },
    });

    if (result.error) {
      addToast("danger", result.error.message || "Something went wrong. Please contact your administrator.");
      setIsSubmitting(false);
    } else {
      onSuccess("Successfully set up BECS Direct Debit");
    }
  };

  const handleSubmitUs = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !fullName || !email) return;

    setIsSubmitting(true);

    const collectResult = await stripe.collectBankAccountForSetup({
      clientSecret,
      params: {
        payment_method_type: "us_bank_account",
        payment_method_data: {
          billing_details: {
            name: fullName,
            email,
          },
        },
      },
    });

    if (collectResult.error) {
      addToast("danger", collectResult.error.message || "Something went wrong. Please contact your administrator.");
      setIsSubmitting(false);
      return;
    }

    if (collectResult.setupIntent?.status === "requires_confirmation") {
      const confirmResult = await stripe.confirmUsBankAccountSetup(clientSecret);

      if (confirmResult.error) {
        addToast("danger", confirmResult.error.message || "Something went wrong. Please contact your administrator.");
        setIsSubmitting(false);
        return;
      }
    }

    onSuccess("Successfully set up ACH Direct Debit");
  };

  const onSuccess = (message: string) => {
    setTimeout(() => {
      getDebitsDetails();
      addToast("success", message);
      setTimeout(() => window.location.replace("../"), 1250);
    }, 8000);
  };

  if (country === "US") {
    return (
      <form id="stripe-debits-form" className="relative flex flex-col" onSubmit={handleSubmitUs}>
        {isLoading || (isSubmitting && <Loading />)}
        <div className="flex flex-col gap-y-3 rounded-lg bg-neutral-surface-gray p-5 lg:w-[573px]">
          <div className="flex flex-col gap-y-1">
            <Typography variant="paragraph">Email</Typography>
            <TextInput
              placeholder="john.smith@example.com"
              size="md"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col gap-y-1">
            <Typography variant="paragraph">Full name</Typography>
            <TextInput
              placeholder="John Smith"
              size="md"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <Typography variant="small" className="text-neutral-dark-gray">
            By clicking &quot;Set up ACH Direct Debit&quot;, you authorize Stripe to debit your bank account for any amounts owed. You may
            revoke this authorization at any time by contacting us. If your bank account details change, please update them before the next
            scheduled payment.
          </Typography>
        </div>
        <div className="mt-4 flex flex-row gap-x-4">
          <Button variant="primary" type="submit" form="stripe-debits-form">
            Set up ACH Direct Debit
          </Button>
          <Button variant="secondary" onClick={() => navigate("../")}>
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form id="stripe-debits-form" className="relative flex flex-col " onSubmit={handleSubmitAu}>
      {isLoading || (isSubmitting && <Loading />)}
      <div className="flex flex-col gap-y-3 rounded-lg bg-neutral-surface-gray p-5 lg:w-[573px]">
        <div className="flex flex-col gap-y-1">
          <Typography variant="paragraph">Bank Account</Typography>
          <AuBankAccountElement options={auBankAccountOptions} />
        </div>
        <div className="flex flex-col gap-y-1">
          <Typography variant="paragraph">Email</Typography>
          <TextInput
            placeholder="john.smith@example.com"
            size="md"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <Typography variant="paragraph">Full name</Typography>
          <TextInput
            placeholder="John Smith"
            size="md"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </div>
        <Typography variant="small" className="text-neutral-dark-gray">
          By providing your bank account details and confirming this payment, you agree to this Direct Debit Request and the{" "}
          <a href="https://stripe.com/au-becs-dd-service-agreement/legal" className="underline">
            Direct Debit Request service agreement
          </a>{" "}
          and authorize Stripe Payments Australia Pty Ltd ACN 160 180 343 Direct Debit User ID number 507156 (&quot;Stripe&quot;) to debit
          your account through the Bulk Electronic Clearing System (BECS) on behalf of Rideminder Australia Pty Ltd (the
          &quot;Merchant&quot;) for any amounts separately communicated to you by the Merchant. You certify that you are either an account
          holder or an authorized signatory on the account listed above.
        </Typography>
      </div>
      <div className="mt-4 flex flex-row gap-x-4">
        <Button variant="primary" type="submit" form="stripe-debits-form">
          Set up BECS Direct Debit
        </Button>
        <Button variant="secondary" onClick={() => navigate("../")}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
